import type { FC } from 'react';
import * as React from 'react';
import {
  Box,
  Card,
  CloseModalIcon,
  FMoneyInput,
  FTextInput,
  IbanInput,
} from '@pflegenavi/web-components';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { getIn } from 'formik';
import type { FormikProps } from 'formik';
import type { IManualPaymentFormValues } from './useManualPaymentValidationSchema';
import { ManualPaymentStatus } from '../../hooks/useConfirmManualPayment';
import { BalanceSagesCard } from '../directDebit/mangoPay/BalanceSagesCard';

interface ManualPaymentFormModalViewProps {
  activeBalance: number | undefined;
  isActiveBalanceLoading: boolean;
  isBalanceInfoLoading: boolean;
  error: boolean;
  manualPaymentStatus: ManualPaymentStatus;
  formik: FormikProps<IManualPaymentFormValues>;
  onAmountChange: (value: number | undefined) => void;
  onSubmit: () => Promise<void>;
  onClose: () => void;
  translations: {
    newPayment: string;
    paymentFailed: string;
    initiatorDetails: string;
    fullName: string;
    email: string;
    pleaseEnterAmount: string;
    topUpAmount: string;
    legalText: string;
    byClickingPay: string;
    cancel: string;
    payNow: string;
  };
  formatCurrency: (amount: number | undefined) => string;
}

export const ManualPaymentFormModalView: FC<
  ManualPaymentFormModalViewProps
> = ({
  activeBalance,
  isActiveBalanceLoading,
  isBalanceInfoLoading,
  error,
  manualPaymentStatus,
  formik,
  onAmountChange,
  onSubmit,
  onClose,
  translations,
  formatCurrency,
}) => {
  if (isBalanceInfoLoading || isActiveBalanceLoading) {
    return (
      <Box sx={{ width: 500 }}>
        <Card
          sx={{
            p: 3,
            height: 300,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ width: 800 }}>
      <Card
        sx={{
          p: 3,
          maxHeight: '95vh',
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Typography fontWeight={700} sx={{ mb: 2 }}>
            {translations.newPayment}
          </Typography>
          <CloseModalIcon onClick={onClose} />
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ pt: 0.2 }}>
              {translations.paymentFailed}
            </Typography>
          </Alert>
        )}
        <Stack sx={{ mb: 3 }} gap={1.5}>
          <Typography>{translations.initiatorDetails}:</Typography>
          <Stack direction="row" gap={2}>
            <FTextInput
              id="name"
              label={translations.fullName}
              name="name"
              formik={formik}
              sx={{ minWidth: 280 }}
            />
            <FTextInput
              sx={{ flex: 1 }}
              id="email"
              label={translations.email}
              name="email"
              type="email"
              formik={formik}
            />
          </Stack>
          <IbanInput
            value={formik.values.iban}
            setValue={(newIban) => formik.setFieldValue('iban', newIban)}
            error={getIn(formik.errors, 'iban')}
          />
        </Stack>
        <Stack direction="row" alignItems="flex-start" sx={{ mb: 3 }} gap={4}>
          <Stack gap={1.5}>
            <Typography>{translations.pleaseEnterAmount}:</Typography>
            <FMoneyInput
              id="amount"
              name="amount"
              formik={formik}
              onChange={onAmountChange}
              label={translations.topUpAmount}
              sx={{ minWidth: 340 }}
            />
          </Stack>
          <BalanceSagesCard
            balance={activeBalance ? activeBalance : 0}
            topUpAmount={
              formik.values.amount?.toString().length > 0
                ? formik.values.amount
                : 0
            }
            disabled={false}
            pendingChargesAmount={0}
          />
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: 'grey.500',
            textAlign: 'justify',
            textAlignLast: 'center',
            mb: 1.5,
          }}
        >
          {translations.legalText}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            marginX: 1,
            mb: 3,
          }}
        >
          {translations.byClickingPay}
        </Typography>
        <Stack direction="row" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            disabled={manualPaymentStatus === ManualPaymentStatus.Loading}
            onClick={onClose}
          >
            {translations.cancel}
          </Button>
          <LoadingButton
            loading={manualPaymentStatus === ManualPaymentStatus.Loading}
            disabled={
              error ||
              !formik.dirty ||
              !formik.isValid ||
              manualPaymentStatus === ManualPaymentStatus.Loading
            }
            variant="contained"
            onClick={onSubmit}
          >
            {translations.payNow}
            {formik.values.amount
              ? ` (${formatCurrency(formik.values.amount)})`
              : ''}
          </LoadingButton>
        </Stack>
      </Card>
    </Box>
  );
};
